import {
  faFile as lightFaFile,
  faFileAudio as lightFaFileAudio,
  faFileCode as lightFaFileCode,
  faFileExcel as lightFaFileExcel,
  faFileImage as lightFaFileImage,
  faFileLines as lightFaFileLines,
  faFilePdf as lightFaFilePdf,
  faFilePowerpoint as lightFaFilePowerpoint,
  faFileVideo as lightFaFileVideo,
  faFileWord as lightFaFileWord,
  faNewspaper as lightFaNewspaper,
} from "@fortawesome/pro-light-svg-icons";
import {
  faFile as regularFaFile,
  faFileAudio as regularFaFileAudio,
  faFileCode as regularFaFileCode,
  faFileCsv as regularFaFileCsv,
  faFileExcel as regularFaFileExcel,
  faFileImage as regularFaFileImage,
  faFileLines as regularFaFileLines,
  faFilePdf as regularFaFilePdf,
  faFilePowerpoint as regularFaFilePowerpoint,
  faFileSvg as regularFaFileSvg,
  faFileVideo as regularFaFileVideo,
  faFileWord as regularFaFileWord,
  faFileZipper as regularFaFileZipper,
  faNewspaper as regularFaNewspaper,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faFileAlt as solidFaFileAlt,
  faFileExcel as solidFaFileExcel,
  faFilePdf as solidFaFilePdf,
  faFilePowerpoint as solidFaFilePowerpoint,
  faFileWord as solidFaFileWord,
} from "@fortawesome/pro-solid-svg-icons";
import {
  DOCUMENT_EXTENSION_KIND_MAP,
  getDocumentIconKind,
  getMimeIconKind,
} from "@utils/shared/media-icon-kind";

export const MEDIA_KIND_META = {
  news: { label: "NEWS", color: "#3949ab" },
  pdf: { label: "PDF", color: "#c62828" },
  word: { label: "DOCX", color: "#1565c0" },
  excel: { label: "XLSX", color: "#2e7d32" },
  powerpoint: { label: "PPTX", color: "#e65100" },
  code: { label: "HTML", color: "#00695c" },
  text: { label: "TXT", color: "#4527a0" },
  image: { color: "#6a1b9a" },
  video: { color: "#0277bd" },
  audio: { color: "#880e4f" },
};

const DOCUMENT_KIND_PALETTE_MAP = {
  pdf: "error.main",
  word: "info.main",
  excel: "success.main",
  powerpoint: "warning.main",
  text: "text.secondary",
};

const ICONS = {
  light: {
    audio: lightFaFileAudio, code: lightFaFileCode, excel: lightFaFileExcel,
    file: lightFaFile, image: lightFaFileImage, news: lightFaNewspaper,
    pdf: lightFaFilePdf, powerpoint: lightFaFilePowerpoint, text: lightFaFileLines,
    video: lightFaFileVideo, word: lightFaFileWord,
  },
  regular: {
    archive: regularFaFileZipper, audio: regularFaFileAudio, code: regularFaFileCode,
    excel: regularFaFileExcel, file: regularFaFile, image: regularFaFileImage,
    news: regularFaNewspaper, pdf: regularFaFilePdf, powerpoint: regularFaFilePowerpoint,
    text: regularFaFileLines, video: regularFaFileVideo, word: regularFaFileWord,
  },
  solid: {
    excel: solidFaFileExcel, file: solidFaFileAlt, pdf: solidFaFilePdf,
    powerpoint: solidFaFilePowerpoint, text: solidFaFileAlt, word: solidFaFileWord,
  },
};

const SPECIAL_REGULAR_EXTENSIONS = { csv: regularFaFileCsv, svg: regularFaFileSvg };
const DYNAMIC_LABELS = {
  image: (subtype) => (subtype === "jpeg" ? "JPG" : subtype.toUpperCase().slice(0, 5)),
  video: (subtype) => (subtype === "quicktime" ? "MOV" : subtype.toUpperCase().slice(0, 5)),
  audio: (subtype) => ({ mpeg: "MP3", ogg: "OGG" }[subtype] || subtype.toUpperCase().slice(0, 5)),
};

const getSubtype = (mimetype) => mimetype?.split("/")[1] ?? "";
const getFallbackLabel = (stackType) => ((stackType && stackType !== "news" && stackType.endsWith("s"))
  ? stackType.slice(0, -1)
  : stackType || "FILE").toUpperCase();
const getSpecialMimeKind = (mimetype) => {
  if (!mimetype) return null;
  if (mimetype.includes("zip") || mimetype.includes("compressed")) return "archive";
  if (mimetype.includes("json") || mimetype.includes("xml")) return "code";
  return null;
};

export const getKindFaIcon = (kind, variant = "regular", fallbackKind = "file") =>
  ICONS[variant]?.[kind] || ICONS[variant]?.[fallbackKind] || null;

export const getMimeTypeLabel = (mimetype, stackType) => {
  const kind = getMimeIconKind(mimetype, stackType);
  const meta = MEDIA_KIND_META[kind];
  if (meta?.label) return { label: meta.label, color: meta.color };
  if (meta?.color && DYNAMIC_LABELS[kind]) {
    return { label: DYNAMIC_LABELS[kind](getSubtype(mimetype)), color: meta.color };
  }
  return { label: getFallbackLabel(stackType), color: "#546e7a" };
};

export const getMimeFaIcon = (mimetype, stackType, variant = "regular", options = {}) => {
  const fallbackKind = options.fallbackToFile === false ? null : "file";
  if (!mimetype && stackType !== "news") {
    return fallbackKind ? getKindFaIcon("file", variant, fallbackKind) : null;
  }
  const kind = getMimeIconKind(mimetype, stackType);
  return getKindFaIcon(kind === "file" ? getSpecialMimeKind(mimetype) || kind : kind, variant, fallbackKind);
};

export const getDocumentFaIcon = (item, variant = "solid") =>
  getKindFaIcon(getDocumentIconKind(item), variant);

export const getDocumentColor = (item) =>
  DOCUMENT_KIND_PALETTE_MAP[getDocumentIconKind(item)] || "text.secondary";

export const getExtensionFaIcon = (extension, variant = "regular", options = {}) => {
  const normalizedExtension = extension?.toLowerCase();
  const fallbackKind = options.fallbackToFile === false ? null : "file";
  if (!normalizedExtension) return getKindFaIcon("file", variant, fallbackKind);
  if (variant === "regular" && SPECIAL_REGULAR_EXTENSIONS[normalizedExtension]) {
    return SPECIAL_REGULAR_EXTENSIONS[normalizedExtension];
  }
  if (["jpeg", "jpg", "png"].includes(normalizedExtension)) {
    return getKindFaIcon("image", variant, fallbackKind);
  }
  if (normalizedExtension === "mp4") return getKindFaIcon("video", variant, fallbackKind);
  if (DOCUMENT_EXTENSION_KIND_MAP[normalizedExtension]) {
    return getKindFaIcon(DOCUMENT_EXTENSION_KIND_MAP[normalizedExtension], variant, fallbackKind);
  }
  return getKindFaIcon("file", variant, fallbackKind);
};
